import { call, delay, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchDriver,
  FetchPersonaInquiry,
  SetPersonaClientCompletedInquiry,
  SetPersonaSessionToken,
  SetDriver,
  SetPersonaStarted,
} from 'src/constants/actionTypes'
import { ApiService, callClientSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { PersonaInquiry } from 'src/interfaces/persona'
import { PersonaInquiryType } from 'src/constants/persona'
import { captureSentryError } from 'src/utils/sentry'
import { getApiErrorMessage } from 'src/utils/errorHandlers'
import { Driver } from 'src/interfaces/driver'
import { CollectDataStepStatus } from 'src/constants/collect-data/enums'

interface FetchPersonaInquiryRequest {
  stepId: string
  inquiryType?: PersonaInquiryType
}

function* onFetchPersonaInquiry(action: AsyncAction<FetchPersonaInquiryRequest, PersonaInquiry>) {
  try {
    const { payload }: { payload: PersonaInquiry } = yield call(
      callClientSecureApi,
      Endpoints.FetchPersonaInquiry,
      {
        apiService: ApiService.Paraworks,
        query: {
          stepId: action.payload.stepId,
          inquiryType: action.payload.inquiryType,
        },
      },
    )

    yield put(FetchPersonaInquiry.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: any) {
    captureSentryError(error, {
      message: '[saga.persona.onFetchPersonaInquiry] error',
    })

    const errorMessage = getApiErrorMessage(error, 'Failed to fetch inquiry')

    yield put(FetchPersonaInquiry.failed(error))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SetPersonaStartedRequest {
  stepId: string
}

function* onSetPersonaStarted(action: AsyncAction<SetPersonaStartedRequest>) {
  try {
    const { stepId } = action.payload

    yield call(callClientSecureApi, Endpoints.SetPersonaStarted, {
      apiService: ApiService.Paraworks,
      method: 'POST',
      body: { stepId },
    })

    yield put(SetPersonaStarted.success())

    if (action.next) {
      action.next(null)
    }
  } catch (error: any) {
    captureSentryError(error, {
      message: '[saga.persona.onSetPersonaStarted] error',
    })

    const errorMessage = getApiErrorMessage(error, 'Failed to start inquiry')

    yield put(SetPersonaStarted.failed(error))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SetPersonaSessionTokenRequest {
  sessionToken: string
  stepId: string
}

function* onSetPersonaSessionToken(action: AsyncAction<SetPersonaSessionTokenRequest>) {
  try {
    const { sessionToken, stepId } = action.payload

    yield call(callClientSecureApi, Endpoints.SetPersonaSessionToken, {
      apiService: ApiService.Paraworks,
      method: 'POST',
      body: {
        sessionToken,
      },
      query: {
        stepId,
      },
    })

    yield put(SetPersonaSessionToken.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (error: any) {
    captureSentryError(error, {
      message: '[saga.persona.onSetPersonaSessionToken] error',
    })

    const errorMessage = getApiErrorMessage(error, 'Failed to set session token')

    yield put(SetPersonaSessionToken.failed(error))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SetPersonaClientCompletedInquiryRequest {
  stepId: string
}

const POLL_DRIVER_MAX_ATTEMPTS = 5
const POLL_DRIVER_DELAY_MS = 2000

function* pollDriverUntilStepProcessed(stepId: string): Generator<any, void, any> {
  for (let attempt = 1; attempt <= POLL_DRIVER_MAX_ATTEMPTS; attempt++) {
    if (attempt > 1) {
      yield delay(POLL_DRIVER_DELAY_MS)
    }

    try {
      const { payload }: { payload: Driver } = yield call(
        callClientSecureApi,
        Endpoints.FetchDriver,
        {
          apiService: ApiService.Paraworks,
        },
      )

      yield put(FetchDriver.success())
      yield put(SetDriver.request(payload))

      const step = payload.collectDataStepsConfigs
        .flatMap((config) => {
          return config.collectDataSteps
        })
        .find((collectDataStep) => {
          return collectDataStep.id === stepId
        })

      if (!step || step.status !== CollectDataStepStatus.Created) {
        return
      }
    } catch (error: any) {
      captureSentryError(error, {
        message: `[saga.persona.pollDriverUntilStepProcessed] error (attempt ${attempt}/${POLL_DRIVER_MAX_ATTEMPTS})`,
      })
    }
  }
}

function* onSetPersonaClientCompletedInquiry(
  action: AsyncAction<SetPersonaClientCompletedInquiryRequest>,
) {
  try {
    yield call(callClientSecureApi, Endpoints.SetPersonaClientCompletedInquiry, {
      apiService: ApiService.Paraworks,
      method: 'POST',
      query: {
        stepId: action.payload.stepId,
      },
    })

    yield put(SetPersonaClientCompletedInquiry.success())

    yield call(pollDriverUntilStepProcessed, action.payload.stepId)

    if (action.next) {
      action.next(null)
    }
  } catch (error: any) {
    captureSentryError(error, {
      message: '[saga.persona.onSetPersonaClientCompletedInquiry] error',
    })

    const errorMessage = getApiErrorMessage(error, 'Failed to set client completed inquiry')

    yield put(SetPersonaClientCompletedInquiry.failed(error))
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* personaSaga() {
  yield takeEvery(FetchPersonaInquiry.type.REQUEST, onFetchPersonaInquiry)
  yield takeEvery(SetPersonaStarted.type.REQUEST, onSetPersonaStarted)
  yield takeEvery(SetPersonaSessionToken.type.REQUEST, onSetPersonaSessionToken)
  yield takeEvery(SetPersonaClientCompletedInquiry.type.REQUEST, onSetPersonaClientCompletedInquiry)
}
